import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import LockOpenOutlinedIcon from "@material-ui/icons/LockOpenOutlined";
import { ListHeaderTitle } from "components/List";
import apiRequest from "utils/apiRequestWithErrorCode";
import EditBtn from "./EditBtn";
import RoleFormModal from "./RoleFormModal";
import RolesSkeleton from "./RolesSkeleton";
import { toRoleRow } from "./utils";
import { UnrestrictedPermissions } from "./styled";

import { RolesConfig } from "@dashboard-v3/api/src/types/roles";
import { UserRole } from "@dashboard-v3/api";
import { MutationArgument, RoleItem } from "./types";

export default function RolesManagement() {
  const { t } = useTranslation("users");
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<RoleItem>(null);
  const swr = useSWR("/roles", rolesFetcher, {
    onError: () => enqueueSnackbar(t("error.fetchRoles"), { variant: "error" }),
  });
  const swrMutation = useSWRMutation("/roles", rolesMutator, {
    onError: () => enqueueSnackbar(t("error.saveRole"), { variant: "error" }),
  });

  function saveRole(role: UserRole) {
    return async (permissions: Record<string, boolean>) => {
      await swrMutation.trigger({ role, permissions }, { throwOnError: false });
    };
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table data-testid="role-list" size="small">
          <TableHead>
            <TableRow style={{ height: "48px" }}>
              <TableCell>
                <ListHeaderTitle variant="button">{t("role")}</ListHeaderTitle>
              </TableCell>
              <TableCell>
                <ListHeaderTitle variant="button">
                  {t("permissions")}
                </ListHeaderTitle>
              </TableCell>
              <TableCell>
                <ListHeaderTitle variant="button">
                  {t("lastUpdated")}
                </ListHeaderTitle>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          {swr.isLoading || swrMutation.isMutating ? (
            <RolesSkeleton />
          ) : (
            <TableBody>
              {(swr.data ?? []).map(item => (
                <TableRow
                  key={item.name}
                  style={{ height: 60 }}
                  data-testid="role-list__item"
                  hover
                >
                  <TableCell>
                    <Typography variant="body2">{t(item.name)}</Typography>
                  </TableCell>
                  <TableCell>
                    {!item.canEdit ? (
                      <UnrestrictedPermissions>
                        <LockOpenOutlinedIcon fontSize="small" />
                        {t("userRole.unrestrictedPermissions")}
                      </UnrestrictedPermissions>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t("userRoleItem.enabledPermissions", {
                          enabled: item.enabled,
                          total: item.total,
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {t(item.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <EditBtn
                      disabled={!item.canEdit}
                      onEdit={() => setEditing(item)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {editing && (
        <RoleFormModal
          isOpen
          roleName={editing.name}
          permissions={editing.permissions}
          isSaving={swrMutation.isMutating}
          onClose={() => setEditing(null)}
          onSave={saveRole(editing.role)}
        />
      )}
    </>
  );
}

async function rolesFetcher() {
  return toRoleRow(await apiRequest<RolesConfig>("GET", "/roles"));
}

function rolesMutator(_, params: MutationArgument) {
  const { arg } = params;
  return apiRequest<RolesConfig>("PATCH", `/roles/${arg.role}`, {
    permissions: arg.permissions,
  });
}
